import { useState } from 'react';
import { Link } from 'react-router-dom';
import { describeError } from '../api/client';
import {
  createSchedule,
  deleteSchedule,
  listProjects,
  listSchedules,
  listTestLists,
  pauseSchedule,
  resumeSchedule,
  scheduleRuns,
} from '../api/endpoints';
import type { ScheduleRunsResponse, ScheduleSummary } from '../api/types';
import { useResource, useToast } from '../state';
import { cronHint, formatRelative, formatTimestamp, pluralize } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { EmptyState, ErrorNotice, LoadingRows } from '../components/Feedback';
import { ConfirmButton, Modal, SelectField, TextField } from '../components/Form';
import { Mono, RunStatusPill, ScheduleStatusPill } from '../components/Pills';

type TargetKind = 'project' | 'testlist';

interface TargetOption {
  id: string;
  name: string;
}

export function SchedulesPage(): JSX.Element {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const schedules = useResource(() => listSchedules(), [], { live: ['schedule.fired', 'schedule.paused'] });

  const toggle = async (schedule: ScheduleSummary): Promise<void> => {
    const action = schedule.status === 'ENABLED' ? pauseSchedule(schedule.id) : resumeSchedule(schedule.id);
    try {
      const result = await action;
      toast.success(
        `“${result.schedule.name}” ${result.schedule.status === 'PAUSED' ? 'paused' : 'resumed'}`,
      );
      schedules.reload();
    } catch (error) {
      toast.error(describeError(error));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Schedules"
        subtitle="Run a project or a suite on a cron. Three consecutive failures pause a schedule automatically, so a broken app cannot page you overnight."
        actions={
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            New schedule
          </button>
        }
      />

      {schedules.error ? (
        <ErrorNotice error={schedules.error} onRetry={schedules.reload} title="Could not load schedules" />
      ) : null}
      {schedules.loading ? <LoadingRows rows={3} label="Loading schedules" /> : null}

      {!schedules.loading && schedules.data && schedules.data.schedules.length === 0 ? (
        <EmptyState
          title="Nothing is scheduled"
          action={
            <button type="button" className="btn" onClick={() => setCreating(true)}>
              Schedule a run
            </button>
          }
        >
          <p>A scheduled run is still a real run: full step results, artifacts and a failure bundle.</p>
        </EmptyState>
      ) : null}

      {schedules.data && schedules.data.schedules.length > 0 ? (
        <ul className="rows">
          {schedules.data.schedules.map((schedule) => (
            <ScheduleRow key={schedule.id} schedule={schedule} onToggle={() => void toggle(schedule)} />
          ))}
        </ul>
      ) : null}

      {creating ? (
        <CreateScheduleDialog
          onClose={() => setCreating(false)}
          onSaved={() => schedules.reload()}
        />
      ) : null}
    </div>
  );
}

function ScheduleRow({
  schedule,
  onToggle,
}: {
  schedule: ScheduleSummary;
  onToggle: () => void;
}): JSX.Element {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const history = useResource<ScheduleRunsResponse | null>(
    () => (open ? scheduleRuns(schedule.id) : Promise.resolve(null)),
    [open, schedule.id],
    { enabled: open },
  );

  return (
    <li className="row row--schedule">
      <div className="row__main">
        <div className="row__title">
          <strong>{schedule.name}</strong>
          <ScheduleStatusPill status={schedule.status} />
          <span className="pill pill--neutral pill--subtle">{schedule.target.kind}</span>
        </div>
        <p className="row__meta">
          {/* The server renders the sentence; we never reimplement cron parsing. */}
          <span className="schedule__text">{schedule.scheduleText}</span>
          <Mono title={schedule.cron}>{schedule.cron}</Mono>
          <span>→ {schedule.targetName}</span>
          <span>{pluralize(schedule.testCount, 'test')}</span>
          {schedule.lastRunAt ? <span>last fired {formatRelative(schedule.lastRunAt)}</span> : null}
        </p>
        {schedule.recipients.length > 0 ? (
          <p className="row__meta">
            <span>notify: {schedule.recipients.join(', ')}</span>
          </p>
        ) : null}
      </div>
      <div className="row__actions">
        <button type="button" className="btn btn--small btn--ghost" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          {open ? 'Hide history' : 'History'}
        </button>
        <button type="button" className="btn btn--small" onClick={onToggle}>
          {schedule.status === 'ENABLED' ? 'Pause' : 'Resume'}
        </button>
        <ConfirmButton
          label="Delete"
          confirmLabel="Really delete?"
          onConfirm={() => {
            void deleteSchedule(schedule.id)
              .then(() => {
                toast.success(`Deleted “${schedule.name}”`);
              })
              .catch((error: unknown) => toast.error(describeError(error)));
          }}
        />
      </div>

      {open ? (
        <div className="row__expansion">
          {history.loading ? <LoadingRows rows={2} label="Loading history" /> : null}
          {history.error ? <ErrorNotice error={history.error} onRetry={history.reload} /> : null}
          {history.data ? (
            <>
              <p className="muted">
                {pluralize(history.data.runs.length, 'run')} in the last 7 days · {history.data.summary.passed}{' '}
                passed, {history.data.summary.failed} failed, {history.data.summary.blocked} blocked,{' '}
                {history.data.summary.cancelled} cancelled
              </p>
              {history.data.runs.length > 0 ? (
                <ul className="history">
                  {history.data.runs.slice(0, 25).map((run) => (
                    <li key={run.id}>
                      <RunStatusPill status={run.status} />
                      {run.failureKind ? <span className="muted">{run.failureKind}</span> : null}
                      <span>{formatTimestamp(run.startedAt)}</span>
                      <Mono>{run.testId.slice(0, 12)}…</Mono>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">No runs in the last seven days.</p>
              )}
            </>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function CreateScheduleDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [name, setName] = useState('');
  const [cron, setCron] = useState('0 3 * * 1-5');
  const [kind, setKind] = useState<TargetKind>('project');
  const [targetId, setTargetId] = useState('');
  const [recipients, setRecipients] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const projects = useResource(() => listProjects(), []);
  const lists = useResource(() => listTestLists(), []);

  const targets: TargetOption[] =
    kind === 'project'
      ? (projects.data?.projects ?? []).map((project) => ({ id: project.id, name: project.name }))
      : (lists.data?.testlists ?? []).map((list) => ({ id: list.id, name: list.name }));

  const effectiveTargetId = targets.some((target) => target.id === targetId) ? targetId : (targets[0]?.id ?? '');

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      const response = await createSchedule({
        name,
        cron,
        target: { kind, id: effectiveTargetId },
        recipients: recipients
          .split(/[,\s]+/)
          .map((value) => value.trim())
          .filter(Boolean),
      });
      toast.success(
        `Schedule “${response.schedule.name}” created`,
        response.schedule.scheduleText,
      );
      onSaved();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      title="New schedule"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void submit()}
            disabled={pending || !name || !effectiveTargetId}
          >
            {pending ? 'Creating…' : 'Create schedule'}
          </button>
        </>
      }
    >
      <TextField label="Name" value={name} onChange={setName} required placeholder="Weekday smoke" />
      <TextField
        label="Cron"
        value={cron}
        onChange={setCron}
        required
        hint={cronHint(cron)}
        placeholder="0 3 * * 1-5"
      />
      <SelectField<TargetKind>
        label="Target kind"
        value={kind}
        onChange={(value) => {
          setKind(value);
          setTargetId('');
        }}
        options={[
          { value: 'project', label: 'project: every test in it' },
          { value: 'testlist', label: 'suite: only its cases' },
        ]}
      />
      <label className="field">
        <span className="field__label" id="schedule-target-label">
          Target
        </span>
        <select
          className="input input--select"
          aria-labelledby="schedule-target-label"
          value={effectiveTargetId}
          onChange={(event) => setTargetId(event.target.value)}
          disabled={targets.length === 0}
        >
          {targets.length === 0 ? <option value="">nothing to target yet</option> : null}
          {targets.map((target) => (
            <option key={target.id} value={target.id}>
              {target.name}
            </option>
          ))}
        </select>
      </label>
      <TextField
        label="Recipients"
        value={recipients}
        onChange={setRecipients}
        hint="Comma separated emails or handles. Optional."
      />
      <p className="muted">
        See all projects on the <Link className="linkish" to="/">Projects tab</Link>.
      </p>
      {error ? <ErrorNotice error={error} title="The schedule was rejected" /> : null}
    </Modal>
  );
}